import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, RequestError, Service } from 'webapp-types';
import { TemplateObjectType, UserAccess } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectBoardComponent from 'webapp/components/form/select-board';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';
import UserAccessPermissionsComponent from 'webapp/components/user/access-permissions';

import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import type Report from 'webapp/models-custom/report';
import type Template from 'webapp/models-custom/template';
import type UserRecord from 'webapp/models/user';

export default class TemplatesAccountPopupEditComponent extends PopupInnerModalComponent<PopupData.Template.Account.Edit> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('request') declare requestService: Service.Request;

	@tracked model: Board | Report | BoardLoop | null = null;
	@tracked selectedObjectId: string | null = null;
	@tracked templateModel!: Template;

	// Board only (Permissions)
	boardAccess: UserAccess | null = null;
	boardForAccess: Board | null = null;
	usersWithPermissions: LabelValuePairNumeric[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const model = this.popupData.model;
		if (!model) {
			const firstItem = this.sortedItems[0];

			if (firstItem) {
				const firstItemValue = String(firstItem.value);
				let firstObject;

				if (this.isBoard) {
					firstObject = this.mmBoardsService.normalBoardModels.find(
						(item) => item.id === firstItemValue,
					);
				} else if (this.isReport) {
					firstObject = this.mmReportsService.normalReportModels.find(
						(item) => item.id === firstItemValue,
					);
				} else {
					firstObject = this.mmBoardLoopsService.normalBoardLoopModels.find(
						(item) => item.id === firstItemValue,
					);
				}

				if (firstObject) {
					this.selectedObjectId = firstObject.id;
					this.model = firstObject;
				}
			}
		} else {
			this.selectedObjectId = model.id;
			this.model = model;
		}

		this.analyticsService.event(TrackingEvent.NEW_ACCOUNT_TEMPLATE, {
			type: this.mmTemplatesService.getTemplateObjectTypeString(this.popupData.objectType),
			origin: this.popupData.trackingOrigin,
		});

		next(() => {
			let templateModel;
			if (this.model instanceof Board || this.model instanceof BoardLoop) {
				const temporaryModel = this.model;

				// Update permissions on board
				temporaryModel.access = UserAccess.PUBLIC;
				temporaryModel.users = this.mmUsersService.activatedUserRecords.map(
					(user: UserRecord) => +user.id!,
				);

				templateModel = this.getTemplateModel(temporaryModel);
			} else {
				const reportModel = this.model!;

				templateModel = this.mmTemplatesService.createNewReportAccountTemplateModel({
					title: reportModel.title,
					objectId: reportModel.numericId!,
				});
			}

			this.templateModel = templateModel;
		});
	}

	get isBoard(): boolean {
		return this.popupData.objectType === TemplateObjectType.BOARD;
	}

	get isBoardLoop(): boolean {
		return this.popupData.objectType === TemplateObjectType.BOARD_LOOP;
	}

	get isReport(): boolean {
		return this.popupData.objectType === TemplateObjectType.REPORT;
	}

	get isObjectSelectDisabled(): boolean {
		return !!this.popupData.model;
	}

	get objectOptions() {
		let items: Board[] | Report[] | BoardLoop[] = [];

		if (this.isBoard) {
			items = this.mmBoardsService.normalBoardModels;
		} else if (this.isReport) {
			items = this.mmReportsService.normalReportModels;
		} else {
			items = this.mmBoardLoopsService.normalBoardLoopModels;
		}

		return items.map((item) => {
			const isDisabled = !item.canCurrentUserEdit,
				isFavorited = item.favorite,
				label = this.getTitleOrNameForObject(item),
				label2 = (item as Board)?.sourcesText || item?.createdText,
				label3 = this.isBoard ? this.getStatusIcon(isDisabled, isFavorited) : '',
				avatar = (item as Board)?.imageMediumOrDefault || item?.image,
				count = this.isBoard ? null : (item as Report).slideCount;

			return {
				value: item.id!, // saved models
				label,
				label2,
				label3,
				avatar,
				bgTheme: item.bgTheme,
				color: item.color,
				order: item.order,
				disabled: isDisabled,
				count,
			};
		});
	}

	get sortedItems() {
		return sortBy(this.objectOptions, 'order', 'value');
	}

	get selectedObject(): Board | Report | BoardLoop | null {
		const selectedObjectId = String(this.selectedObjectId);
		let selectedObject;

		if (this.isBoard) {
			selectedObject = this.mmBoardsService.normalBoardModels.find(
				(item) => item.id === selectedObjectId,
			);
		} else if (this.isReport) {
			selectedObject = this.mmReportsService.normalReportModels.find(
				(item) => item.id === selectedObjectId,
			);
		} else {
			selectedObject = this.mmBoardLoopsService.normalBoardLoopModels.find(
				(item) => item.id === selectedObjectId,
			);
		}

		return selectedObject || null;
	}

	get selectedObjectTitleOrName(): string {
		return this.getTitleOrNameForObject(this.selectedObject);
	}

	getStatusIcon(isDisabled: boolean, isFavorited: boolean): string {
		let statusText = '';

		if (isFavorited) {
			statusText += '<ui-icon name="StarFill" size="sm"></ui-icon>';
		}

		if (isDisabled) {
			statusText +=
				'<ui-icon name="Info" size="sm" title="Due to your user role, you don\'t have permissions to make template out of this board!"></ui-icon>';
		}

		return statusText;
	}

	get showBoardAccess(): boolean {
		return this.isBoard || this.isBoardLoop;
	}

	get modelForAccessChoice() {
		// only when this.showBoardAccess: true
		return this.model as Board | BoardLoop;
	}

	@action
	save(): void {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;

		const data: {
			template: ReturnType<Template['toSerializedJSON']>;
			boardAccess?: UserAccess | null;
			usersForPermissions?: number[];
		} = {
			template: this.templateModel.toSerializedJSON(),
		};

		if (this.isBoard || this.isBoardLoop) {
			data.boardAccess = this.boardAccess;
			if (this.boardAccess === UserAccess.RESTRICTED) {
				data.usersForPermissions = this.usersWithPermissions.map((user) => +user.value);
			} else {
				data.usersForPermissions = [];
			}
		}

		const loadingLayer = this.infolayerService.loading('Creating template...');

		this.requestService
			.apiWithAsyncReply('POST', '/templates', data, {}, (payload) => {
				// store push handled automatically by socket.ts triggerBgEventDone
				loadingLayer.ok('Template created!');

				if (this.isBoardLoop) {
					this.mmTemplatesService.trackBoardLoopTemplateEvent(
						this.templateModel,
						this.popupData.trackingOrigin || '',
						TrackingEvent.TEMPLATE_CREATED,
					);

					payload.boards?.forEach((board) => {
						const boardModel = this.mmBoardsService.normalBoardModels.find(
							(item) => item.id === String(board.id),
						);

						this.mmBoardsService.triggerDataboardCreated(
							{ from: 'loop_saved_as_account_template' },
							boardModel,
						);
					});
				} else {
					this.mmTemplatesService.trackEvent(
						this.templateModel,
						TrackingEvent.TEMPLATE_CREATED,
						{ origin: this.popupData.trackingOrigin },
					);
				}

				this.close();
				return true; // done, unsubscribe from event
			})
			.catch((errorData: RequestError) => {
				loadingLayer.close();
				this.requestService.showAllValidationErrors(errorData);
			})
			.finally(() => {
				this.submitInProgress = false;
			});
	}

	@action
	changeSelectedObjectId(value: string): void {
		this.selectedObjectId = value;
		this.templateModel.objectId = +value;
		this.templateModel.title = this.selectedObjectTitleOrName;
	}

	@action
	changeAccessAndPermissions(access: UserAccess, users: LabelValuePairNumeric[]): void {
		this.boardAccess = access;
		this.usersWithPermissions = users;
	}

	private getTemplateModel(model: Board | BoardLoop): Template {
		if (model instanceof Board) {
			return this.mmTemplatesService.createNewBoardAccountTemplateModel({
				title: model.name,
				objectId: model.numericId!,
			});
		} else {
			return this.mmTemplatesService.createNewBoardLoopAccountTemplateModel({
				title: model.title,
				objectId: model.numericId!,
			});
		}
	}

	private getTitleOrNameForObject(object: Board | Report | BoardLoop | null): string {
		return (object as Board)?.name || (object as Report | BoardLoop)?.title || '';
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="FolderUpload" @size="lg" />
			Save template
		</div>

		<div class="content">
			<div class="row">
				<FormSelectBoardComponent
					name="board"
					@selected={{this.selectedObjectId}}
					@options={{this.objectOptions}}
					@onChange={{this.changeSelectedObjectId}}
					@isNumeric={{false}}
					@isRequired={{true}}
					@isDisabled={{this.isObjectSelectDisabled}}
					@extraClass="cs-image"
					@dropdownSearchPlaceholder="Search"
					@showFloatlabel={{false}}
					@tooltipOnSelectionFromProperty=""
				/>
			</div>
			<RowErrorComponent @items={{this.templateModel.errors.objectId}} />

			<div class="row">
				<FormInputComponent
					name="title"
					maxlength="50"
					disabled={{this.submitInProgress}}
					@value={{this.templateModel.title}}
					placeholder="Title"
				/>
				<RowErrorComponent @items={{this.templateModel.errors.title}} />
			</div>

			{{!-- <div class="row">
        <Form::Input
            name="description"
            maxlength="50"
            placeholder="Description (optional)"
            disabled={{this.submitInProgress}}
            @value={{this.templateModel.description}}
        />
    </div> --}}

			{{#if this.showBoardAccess}}
				<UserAccessPermissionsComponent
					@model={{this.modelForAccessChoice}}
					@onChange={{this.changeAccessAndPermissions}}
					@introText="Who should have access to this template?"
					@onContentChange={{this.scheduleReposition}}
				/>
			{{/if}}

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
